import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import KenTextLabel from '../KenTextLabel';
import KenError from '../KenError';
import PhoneInput from 'react-phone-input-2';
import { Box, Divider, Typography } from '@mui/material';
import KenIcon from '../KenIcon';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '../../Assets/Icons/error.png';

const useStyles = makeStyles(() => ({
  inputFieldLabel: {
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '100%',
    textTransform: 'capitalize',
    color: '#505F79',
  },
  phoneContainer: {
    display: 'flex',
    '& .phone-input': {
      fontFamily: 'Poppins,sans-serif',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '100%',
      color: '#505F79',
      padding: '16.5px 14px',
      order: 2,
      background: '#F3F5F7',
      border: 0,
      height: '3.4375rem',
      borderRadius: '50px',
      marginLeft: '11px',
      '&::placeholder': {
        fontSize: '16px',
        lineHeight: '100%',
        color: '#A8AFBC',
        fontFamily: 'Poppins,sans-serif',
      },
    },
    '& .phone-dropdown': {
      position: 'relative',
      borderRadius: '50px',
      order: 1,
      border: 0,
      background: '#F3F5F7',
      '&.open': {
        background: '#F3F5F7',
        '& .selected-flag': {
          background: '#F3F5F7',
        },
      },
      '& .selected-flag': {
        width: '50px',
        padding: '10px',
        '&:hover': {
          background: '#F3F5F7',
        },
      },
    },
  },
}));

export default function KenPhoneInput({
  value,
  onChange,
  error,
  success,
  ui,
  country = 'in',
  ...props
}) {
  const classes = useStyles();

  // Default dial code
  const defaultDial = country === 'in' ? '91' : '';
  const [phoneValue, setPhoneValue] = useState(value || `+${defaultDial}`);
  const [currentDial, setCurrentDial] = useState(defaultDial);

  // Sync with parent value
  useEffect(() => {
    if (value && value !== phoneValue) {
      setPhoneValue(value);
    }
  }, [value]);

  const handlePhoneChange = (val, data) => {
    const newDial = data?.dialCode || currentDial;

    // Update dial code if country changes
    if (newDial !== currentDial) {
      setCurrentDial(newDial);

      // Keep only the phone number part after the new dial code
      const digits = val.replace(/\D/g, '');
      const cleaned = digits.slice(newDial.length);
      const updated = `+${newDial}${cleaned}`;

      setPhoneValue(updated);
      onChange?.(updated);
      return;
    }

    // Prevent deleting the dial code
    const requiredPrefix = `+${newDial}`;
    if (!val.startsWith(requiredPrefix)) {
      const digits = val.replace(/\D/g, '');
      const cleaned = digits.slice(newDial.length);
      const restored = `+${newDial}${cleaned}`;

      setPhoneValue(restored);
      onChange?.(restored);
      return;
    }

    // Normal typing
    setPhoneValue(val);
    onChange?.(val);
  };

  return (
    <>
      <KenTextLabel
        label="Phone Number"
        labelClassName={classes.inputFieldLabel}
        required
      />

      <Box display="flex" alignItems="flex-start">
        <PhoneInput
          {...props}
          country={country}
          value={phoneValue}
          onChange={handlePhoneChange}
          containerClass={classes.phoneContainer}
          inputClass="phone-input"
          buttonClass="phone-dropdown"
          placeholder="Enter Number"
          countryCodeEditable={false} // prevent user from deleting the dial code
        />

        {error && (
          <Box width="16px" height="16px" margin="19px 8px">
            <KenIcon iconType="img" icon={ErrorIcon} variant="extraSmall" />
          </Box>
        )}
      </Box>

      {error && <KenError message={error} single />}
      
      {success && (
        <Box display="flex" alignItems="flex-start" pt={1}>
          <Typography component="span">
            {success.icon || <DoneIcon fontSize="small" style={{ color: '#158467' }} />}
          </Typography>
          <Typography component="span" pl={1}>
            {success.message}
          </Typography>
        </Box>
      )}

      {ui?.divider && (
        <Box mt={2} mb={2}>
          <Divider />
        </Box>
      )}
    </>
  );
}

